import LoadingBar from "@components/ui/LoadingBar";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useEffect, useState } from "react";

import DataGrid from "@components/ui/tables/DataGrid";
import { transformOrdresToTable } from "@components/ui/tables/transformToTables";
import { useDataStore } from "@store/user_org";
import { useOrdreStore } from "@store/ordreStore";
import { isGUEST } from "@helpers/accessHelpers";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { searchOrdresFull } from "@services/algolia";

const statusTabs = [
  { value: "all", label: "Tous" },
  { value: "a_preparer", label: "A préparer" },
  { value: "commande", label: "Commande" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réception" },
  { value: "en_cours", label: "En cours" },
  { value: "devis", label: "Devis" },
  { value: "accord", label: "Accord" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
];

export default function OrdresScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const isGuest = isGUEST(userData);
  const {
    ordres,
    isLoading,
    isRefreshing,
    hasMore,
    hasPrev,
    myOnly,
    fetchOrdres,
    fetchNextPage,
    fetchPrevPage,
    setSort,
    setMyOnly,
    removeOrdres,
  } = useOrdreStore();

  const [table, setTable] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("all");

  useEffect(() => {
    if (orgData?.id) {
      const assignedTo = myOnly ? userData?.id : undefined;
      fetchOrdres(orgData.id, activeTab, assignedTo);
    }
  }, [orgData?.id, activeTab, myOnly]);

  useEffect(() => {
    const table = transformOrdresToTable(ordres, "all");
    setTable(table);
  }, [ordres]);

  const handleDeleteSelected = () => {
    removeOrdres(selectedIds);
    showNotification({ type: "success", message: `${selectedIds.length} ordre(s) supprimé(s)` });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  const handleMyOnlyToggle = (e: any) => {
    setMyOnly(e.target.checked);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} ordre(s) ?`}
          message="Cette action est irréversible. Les ordres et toutes leurs données seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
          confirmText="CONFIRMER"
        />
      )}

      {/* Header */}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col md:flex-row w-full max-w-1p2k justify-between">
          <div className="flex flex-col w-full md:w-500">
            <Text1>Ordres de réparation</Text1>
            <Text2>Retrouvez tous vos ordres de réparations ici.</Text2>
          </div>
          <div className="flex flex-row gap-2 items-center">
            {selectedIds.length > 0 && (
              <SecondaryButton
                startIcon={<Icon1 name="delete" size={18} />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                {`Supprimer la sélection (${selectedIds.length})`}
              </SecondaryButton>
            )}
            {!isGuest && (
              <Button label="Nouvel ordre" onClick={() => {}} />
            )}
          </div>
        </div>
      </div>

      {/* DataGrid area */}
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col gap-3 w-full">
        {(isLoading || isRefreshing) && <LoadingBar />}
        <div className="flex-1 min-h-0 max-w-1p2k w-full">
            {table && (
              <DataGrid
                selectable={true}
                table={table}
                hide_lines={true}
                tabs={statusTabs}
                activeTab={activeTab}
                onTabChange={(tab) => setActiveTab(tab)}
                onSearch={async (query) => {
                  if (!orgData?.id) return [];
                  return searchOrdresFull(query, orgData.id);
                }}
                onSearchSelect={(option) => navigateTo({ route: "/ordre/" + option.value })}
                onClick={(item) => navigateTo({ route: "/ordre/" + item.id })}
                handleSelectionModelChange={(ids) => setSelectedIds(ids)}
                hasNext={hasMore}
                hasPrev={hasPrev}
                onNext={() => orgData?.id && fetchNextPage(orgData.id)}
                onPrev={() => orgData?.id && fetchPrevPage(orgData.id)}
                handleSortChange={(model) => {
                  if (orgData?.id && model.length > 0)
                    setSort(orgData.id, model[0].field, model[0].sort);
                }}
                extraSearchContent={
                  <CheckBoxInput
                    checked={myOnly}
                    onChange={handleMyOnlyToggle}
                    label="Mes ordres"
                  />
                }
              />
            )}
          </div>
      </div>
    </div>
  );
}
